import { Button, KEYBOARD_KEYS, PopoverContext, Tag } from "@netapp/bxp-design-system-react";
import { NetAppLogoIcon as NetAppLogo } from "@netapp/bxp-design-system-react/icons/netapp-services";
import { MenuIcon } from "@netapp/bxp-style/react-icons/Navigation";
import classNames from "classnames";
import GlobalSearchWidget from "modules/global-search/components/GlobalSearchWidget";
import NotificationsMenu from "modules/notifications/components/NotificationsMenu";
import { isVirtualTourEnabled } from "offline/utils";
import React, { forwardRef, lazy, memo, Suspense, useCallback, useImperativeHandle, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from 'react-router-dom';
import { isNewTenancyEnabled } from "utils/localstorage";
import { allMenus as i18nNamespace } from "../../../i18n/namespace";
import AccountMenu from "./headerMenus/AccountMenu";
import ConnectorMenu from "./headerMenus/ConnectorMenu";
import HelpMenu, { HelpBtn } from "./headerMenus/HelpMenu";
import OrganizationMenu from "./headerMenus/OrganizationMenu";
import SafeModeTag from "./headerMenus/SafeModeTag";
import ScopeMenu from "./headerMenus/ScopeMenu";
import NclScopeMenu from "./headerMenus/NclScopeMenu";
import UserMenu from "./headerMenus/userMenu";
import WorkspaceMenu from "./headerMenus/WorkspaceMenu";
import styles from "./MainHeader.module.scss";
import { isNcl } from "utils/app/utils";
import { useFeatureFlag } from "utils/useFeatureFlag";
import { PREFERENCES_KEYS } from '../middlewares/userPreferencesMiddleware';
const ChatContainer = lazy(() => import('./headerMenus/AiAssistant/components/AiAssistantTrigger'));
const GLOBAL_SEARCH_FEATURE = "NetApp.Console/global-search";

export default memo(forwardRef(({navRef, skipTargetRef, noOrganizationForUser}, ref) => {
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const mainLinkRef = useRef(null);
    const isShowingCategoriesNav = useSelector(state => state.menu.activeWidget === 'leftNav');

    // Expose focusMainLink method to parent component
    // This allows the parent component to focus the main link when needed
    useImperativeHandle(ref, () => ({
        focusMainLink: () => {
            mainLinkRef.current?.focus();
        }
    }), [mainLinkRef]);

   const handleSkipNavigationKeyDown = useCallback((event) => {
        if (event.key === KEYBOARD_KEYS.ENTER || event.key === KEYBOARD_KEYS.SPACE) {
            event.preventDefault();

            if (isShowingCategoriesNav) {
                dispatch({
                    type: "MENU:CLOSE-WIDGET"
                });
            }

            if (skipTargetRef?.current?.focus) {
                skipTargetRef.current.focus();
            }
        }
   }, [skipTargetRef, isShowingCategoriesNav, dispatch]);

    const isDemo = useSelector(state => state.tenancy.isDemo);
    const { selectedAccountId, forbidden, isSmtpConfigured } = useSelector(state => state.tenancy);
    const { data: isSmtpSkipped = false } = useSelector(state => state.userPreferences[PREFERENCES_KEYS.SMTP_SETUP_SKIPPED_PREFERENCE_KEY] || {});
    const userHasAccount = selectedAccountId && !forbidden;
    const nclSmtpNotConfigured = isNcl() && isSmtpConfigured === false;
    const showingSmtpSetup = nclSmtpNotConfigured && !isSmtpSkipped;
    const isGlobalSearchOpen = useSelector(state => state.globalSearch?.isOpen);
    const isGlobalSearchEnabled = useFeatureFlag(GLOBAL_SEARCH_FEATURE);
    const isAIDP = process.env.REACT_APP_IS_AIDP === "true";
    // Get the Feature flag state from Redux state.
    const isTenancyV4 = isNewTenancyEnabled();
    const vendorizedProductName = useSelector(state => state.app.vendorizedProductName);
    const productName = isAIDP ? t("data_engine_console") : vendorizedProductName;
    const isVirtualTourMode = isVirtualTourEnabled();

    const handleOpenLeftNav = React.useCallback(() => {
        if (isShowingCategoriesNav) {
            dispatch({
                type: "MENU:CLOSE-WIDGET"
            });
        } else {
            dispatch({
                type: "MENU:OPEN-WIDGET",
                payload: 'leftNav'
            });
        }
    }, [dispatch, isShowingCategoriesNav]);

    const handleMenuButtonKeyDown = useCallback((event) => {
        if (event.key === KEYBOARD_KEYS.ENTER) {
            event.preventDefault();
            event.stopPropagation();
            handleOpenLeftNav();
        } else if (event.key === KEYBOARD_KEYS.TAB && !event.shiftKey && isShowingCategoriesNav) {
            // When Tab is pressed and CategoryNav is open, focus the first element
            event.preventDefault();
            if (navRef?.current?.focusFirstNavItem) {
                navRef.current.focusFirstNavItem();
            }
        }
    }, [handleOpenLeftNav, isShowingCategoriesNav, navRef]);

    return (<PopoverContext Component="header" className={styles['container']}>
        <div className={styles["title"]}>
            <Button onClick={handleOpenLeftNav} onKeyDown={handleMenuButtonKeyDown} variant="icon" color="secondary" isDisabled={noOrganizationForUser || showingSmtpSetup} className={classNames(styles['left-nav-trigger-btn'], {[styles['is-active']]: isShowingCategoriesNav})}><MenuIcon size={24} /></Button>
            <Link ref={mainLinkRef} className={styles['main-link']} to="/"><NetAppLogo />{productName}</Link>
            {isNcl() && <Tag level={12} color="tag-2" style={{ marginLeft: 22 }}> {t('local')}</Tag>}
            {/* Show Virtual Tour is like a superset of Demo env so only one pill to be displayed */}
            {isVirtualTourMode && <Tag level={12} color="chart-9" style={{ marginLeft: 22 }}> {t('virtual_tour')}</Tag>}
            {isDemo && !isVirtualTourMode && <Tag level={12} color="chart-9" style={{ marginLeft: 22 }}>Demo</Tag>}
            {process.env.REACT_APP_IS_DARK !== "true" && <SafeModeTag />}
        </div>
       <a className={styles['skip-content']} href="#" onKeyDown={handleSkipNavigationKeyDown}>
            {t('skip_navigation')}
        </a>
        <div className={styles['menu-widgets']}>
            {userHasAccount && !isAIDP && !showingSmtpSetup && <>
                {isNcl() && isGlobalSearchEnabled && <GlobalSearchWidget />}
                {isNcl() && <>
                    {isTenancyV4 && <NclScopeMenu />}
                    <Suspense fallback={null}><ChatContainer /></Suspense>
                </>}
                {!isGlobalSearchOpen && <>
                    {/* Show Organization and Scope menu when Tenancy v4 flag is enabled else show existing Account and Workspace menu */}
                    {isTenancyV4 ? (!isNcl() && <OrganizationMenu />) : <AccountMenu />}
                    {isTenancyV4 ? (!isNcl() && <ScopeMenu />) : <WorkspaceMenu />}
                    {!isNcl() && <ConnectorMenu />}
                </>}
                {(process.env.REACT_APP_IS_DARK !== "true" || isNcl()) && <NotificationsMenu />}
                {!isNcl() && <HelpMenu />}
            </>
            }
            {isAIDP && <HelpBtn/>}
            <UserMenu/>
        </div>
    </PopoverContext>)
}));
